/**
 * Unified error response handler for consistent API error formatting
 * 
 * Standardizes error responses across all modules to:
 * - Provide consistent error format
 * - Include proper error codes
 * - Add request context (requestId, userId)
 * - Log with proper severity levels
 * - Handle different error types appropriately
 */

import type { FastifyError, FastifyReply, FastifyRequest } from 'fastify'
import { ZodError } from 'zod'
import { createLogger } from '../utils/logger.js'
import { AppError } from '../errors/index.js'
// Re-export error classes for convenience
export { AppError, ValidationError, NotFoundError, ConflictError, UnauthorizedError, ForbiddenError } from '../errors/index.js'

const logger = createLogger('ErrorHandler')
const MAX_STACK_FRAMES = Number(process.env.LOG_STACK_FRAMES ?? '5')

function trimStack(stack?: string, maxFrames: number = MAX_STACK_FRAMES): string | undefined {
  if (!stack) return undefined
  const lines = stack.split('\n')
  if (!lines.length) return undefined
  const header = lines[0]
  const frames = lines.slice(1).filter((line) => {
    const lower = line.toLowerCase()
    return !(
      lower.includes('node:internal') ||
      lower.includes('node_modules/pino') ||
      lower.includes('fastify/lib') ||
      lower.includes('diagnostics_channel') ||
      lower.includes('async_hooks')
    )
  })

  const limitedFrames = frames.slice(0, maxFrames)
  return [header, ...limitedFrames].join('\n')
}

/**
 * Standard error response format
 */
export interface ErrorResponse {
  error: {
    code: string
    message: string
    statusCode: number
    requestId?: string
    userId?: string
    context?: Record<string, unknown>
    timestamp: string
    details?: Array<{
      path: string
      message: string
      code: string
    }>
    stack?: string
  }
}

/**
 * Map error types to HTTP status codes and error codes
 */
export const ERROR_CODES: Record<string, { statusCode: number; code: string }> = {
  ValidationError: { statusCode: 400, code: 'VALIDATION_ERROR' },
  NotFoundError: { statusCode: 404, code: 'NOT_FOUND_ERROR' },
  ConflictError: { statusCode: 409, code: 'CONFLICT_ERROR' },
  UnauthorizedError: { statusCode: 401, code: 'UNAUTHORIZED_ERROR' },
  ForbiddenError: { statusCode: 403, code: 'FORBIDDEN_ERROR' },
  RateLimitError: { statusCode: 429, code: 'RATE_LIMIT_EXCEEDED' },
  QuotaExceededError: { statusCode: 429, code: 'QUOTA_EXCEEDED' },
}

/**
 * Unified error handler - Use as Fastify setErrorHandler
 * 
 * @example
 * fastify.setErrorHandler(errorHandler)
 */
export async function errorHandler(
  error: FastifyError | Error | AppError,
  request: FastifyRequest,
  reply: FastifyReply
): Promise<void> {
  const requestId = request.id || 'unknown'
  const userId = request.user?.sub || request.user?.uid || undefined
  const safeStack = trimStack((error as any).stack)

  // Handle Zod validation errors
  if (error instanceof ZodError) {
    const statusCode = 400
    const response: ErrorResponse = {
      error: {
        code: 'VALIDATION_ERROR',
        message: 'Request validation failed',
        statusCode,
        requestId,
        userId,
        context: { fieldCount: error.errors.length },
        timestamp: new Date().toISOString(),
        details: error.errors.map((e) => ({
          path: e.path.join('.'),
          message: e.message,
          code: e.code,
        })),
      },
    }

    logger.warn('Validation error', {
      requestId,
      userId,
      errorCount: error.errors.length,
      path: request.url,
      method: request.method,
      stack: safeStack
    })

    return reply.status(statusCode).send(response)
  }

  // Handle app errors (custom error types)
  if (error instanceof AppError) {
    const statusCode = error.statusCode || 500
    const errorCode = error.errorCode || 'INTERNAL_ERROR'

    const response: ErrorResponse = {
      error: {
        code: errorCode,
        message: error.message,
        statusCode,
        requestId,
        userId,
        context: error.context,
        timestamp: new Date().toISOString(),
        ...(process.env['NODE_ENV'] === 'development' && { stack: error.stack }),
      },
    }

    // Log appropriate level based on status code
    if (statusCode >= 500) {
      logger.error(error.message, {
        requestId,
        userId,
        errorCode,
        statusCode,
        error: error.toJSON(),
        stack: safeStack
      })
    } else if (statusCode >= 400) {
      logger.warn(error.message, {
        requestId,
        userId,
        errorCode,
        statusCode,
        context: error.context,
        stack: safeStack
      })
    }

    return reply.status(statusCode).send(response)
  }

  // Handle premature stream close (client disconnect)
  if ((error as any).code === 'ERR_STREAM_PREMATURE_CLOSE' || error.message === 'premature close') {
    const statusCode = 400
    const response: ErrorResponse = {
      error: {
        code: 'CLIENT_DISCONNECT',
        message: 'Request stream closed prematurely',
        statusCode,
        requestId,
        userId,
        timestamp: new Date().toISOString(),
      },
    }

    logger.warn('Client disconnected prematurely', {
      requestId,
      userId,
      path: request.url,
      method: request.method
    })

    return reply.status(statusCode).send(response)
  }

  // Handle Fastify errors
  if ('statusCode' in error && typeof error.statusCode === 'number') {
    const statusCode = error.statusCode || 500
    const errorCode = 'FASTIFY_ERROR'

    const response: ErrorResponse = {
      error: {
        code: errorCode,
        message: error.message || 'An error occurred',
        statusCode,
        requestId,
        userId,
        timestamp: new Date().toISOString(),
        ...(process.env['NODE_ENV'] === 'development' && { stack: error.stack }),
      },
    }

    if (statusCode >= 500) {
      logger.error(error.message, {
        requestId,
        userId,
        statusCode,
        error: error,
        stack: safeStack
      })
    }

    return reply.status(statusCode).send(response)
  }

  // Handle generic errors
  const statusCode = 500
  const errorCode = 'INTERNAL_SERVER_ERROR'
  const message = process.env['NODE_ENV'] === 'production' ? 'Internal Server Error' : error.message

  const response: ErrorResponse = {
    error: {
      code: errorCode,
      message,
      statusCode,
      requestId,
      userId,
      timestamp: new Date().toISOString(),
      ...(process.env['NODE_ENV'] === 'development' && { stack: (error as any).stack }),
    },
  }

  logger.error('Unhandled error', {
    requestId,
    userId,
    error: error instanceof Error ? error.message : String(error),
    type: error.constructor.name,
    stack: safeStack
  })

  return reply.status(statusCode).send(response)
}

/**
 * Try-catch wrapper for async route handlers
 * Automatically catches errors and passes to error handler
 * 
 * @example
 * app.get('/user/:id', asyncHandler(async (req, reply) => {
 *   const user = await getUserById(req.params.id)
 *   return reply.send(user)
 * }))
 */
export function asyncHandler(fn: (req: FastifyRequest, reply: FastifyReply) => Promise<void>) {
  return async (req: FastifyRequest, reply: FastifyReply) => {
    try {
      await fn(req, reply)
    } catch (error) {
      await errorHandler(error as FastifyError | Error | AppError, req, reply)
    }
  }
}

/**
 * 404 Not Found handler
 */
export async function notFoundHandler(
  request: FastifyRequest,
  reply: FastifyReply
): Promise<void> {
  const requestId = request.id || 'unknown'
  const userId = request.user?.sub || request.user?.uid || undefined

  const response: ErrorResponse = {
    error: {
      code: 'NOT_FOUND_ERROR',
      message: `Route not found`,
      statusCode: 404,
      requestId,
      userId,
      context: {
        method: request.method,
        path: request.url,
      },
      timestamp: new Date().toISOString(),
    },
  }

  logger.warn('Route not found', {
    requestId,
    userId,
    method: request.method,
    path: request.url,
  })

  return reply.status(404).send(response)
}

/**
 * HTTP error response helper for controllers
 * Use in controllers for consistent error throwing
 * 
 * @example
 * if (!user) {
 *   throw new NotFoundError('User not found', { userId })
 * }
 */
export function createErrorResponse(
  statusCode: number,
  code: string,
  message: string,
  context?: Record<string, unknown>
) {
  const response: ErrorResponse = {
    error: {
      code,
      message,
      statusCode,
      context,
      timestamp: new Date().toISOString(),
    },
  }
  return response
}

// Error classes are exported at top of file
